import * as path from "node:path";
import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { appHealthFailureCopy, checkAppHealth, readAppSecrets } from "@m8t-stack/github-app-auth";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { resolveFoundryProject } from "../../lib/foundry-project.js";
import { checkPreconditions } from "../../lib/preconditions.js";
import { getAgentVersion } from "../../lib/foundry-agent-get.js";
import { deployHostedWorker } from "../../lib/coder-deploy.js";
import { resolvePersona } from "../../lib/persona.js";
import { colors, renderJson, resolveOutputMode } from "../../lib/output.js";
import { discoverKvUri, resolvePlatformRepoRoot } from "../../lib/brain-paths.js";
import { resolveAppInstallation } from "../../lib/app-install.js";
import { ensureDeliveryGrant, normalizeKvUri } from "../../lib/delivery-grant.js";
import { grantContributor, grantUserAccessAdmin, callerCanAssignRoles } from "../../lib/rbac.js";
import { enableA2a } from "../../lib/a2a-enable.js";
import { resolveBridgeUrl } from "../../lib/bridge-url.js";
import { listModelQuota, modelQuotaVerdict } from "../../lib/quota.js";
import { assertPlatformLive } from "../../lib/platform-live.js";
import { stagePublicImageIfNeeded } from "../../lib/image-staging.js";
import { makeWarningSink } from "../../lib/warnings.js";
import {
  resolveAcs,
  emailFeatureFrom,
  platformSuffixFromKvUri,
  classifyGrantScope,
  resourceGroupFromArmId,
} from "../../lib/acs-resolve.js";
import { readStamp, readStampOutcome, writeStamp } from "../../lib/platform-stamp.js";
import { discoverGateway } from "../../lib/discovery.js";
import type { EmailFeatureStamp, InstalledStamp } from "@m8t-stack/platform-release";
import type { TokenCredential } from "@azure/identity";
import { CommunicationServiceManagementClient } from "@azure/arm-communication";

const SIZE_PRESETS: Partial<Record<string, { cpu: string; memory: string }>> = {
  small: { cpu: "0.5", memory: "1Gi" },
  medium: { cpu: "1", memory: "2Gi" },
  large: { cpu: "2", memory: "4Gi" },
};
const DEFAULT_REGISTRY = "ghcr.io/m8t-labs";
const DEFAULT_IMAGE = "m8t-azure-executor";
const DEFAULT_TAG = "v0.1.4"; // public GHCR pin (agents/azure-executor/pyproject.toml#version); bump when a new agent version ships
const DEFAULT_MODEL = "gpt-5-mini";
const NAME_RE = /^[a-z0-9-]+$/;

export class AzureExecDeployCommand extends M8tCommand {
  static paths = [["azure-exec", "deploy"]];
  static usage = Command.Usage({
    description: "Deploy the Azure executor as a hosted Foundry worker (az CLI + tiered ops).",
    details:
      "Creates a hosted agent version from a container image (defaults to the public GHCR image ghcr.io/m8t-labs/m8t-azure-executor — no local build needed), grants its identity Foundry User + Contributor (at --scope) + Key Vault Secrets User (brain KV), polls to active, and a2a-enables it as a target. Override with --image/--image-tag for a bring-your-own-registry image (e.g. a private ACR ref, which must be pushed first). Contributor scope is REQUIRED — pass --scope or --resource-group. Pass --grant-access-admin to additionally grant User Access Administrator (enables human-approved Tier-2 role/delete ops). Post-2026-06-25 Foundry projects require an authenticated pull, so the public image (default ghcr.io/m8t-labs/m8t-azure-executor) is staged into the customer's Azure Container Registry first (created if missing, then `az acr import` — server-side, no local build) and deployed from that ACR ref; re-running with a newer --image-tag updates the image.",
    examples: [
      [
        "Deploy scoped to a resource group",
        "$0 azure-exec deploy azexec --resource-group rg-test --brain m8t-labs/azure-exec-smoke-brain --gateway-url https://<gw>/api/a2a/mcp",
      ],
    ],
  });

  name = Option.String();
  image = Option.String("--image");
  imageTag = Option.String("--image-tag");
  size = Option.String("--size");
  scope = Option.String("--scope");
  resourceGroup = Option.String("--resource-group");
  modelDeployment = Option.String("--model-deployment");
  env = Option.Array("--env");
  brain = Option.String("--brain");
  kvUri = Option.String("--kv-uri");
  gatewayUrl = Option.String("--gateway-url");
  endpoint = Option.String("--endpoint");
  subscription = Option.String("--subscription");
  output = Option.String("--output");
  skipQuotaCheck = Option.Boolean("--skip-quota-check", false);
  grantAccessAdmin = Option.Boolean("--grant-access-admin", false);
  /** Deprecated no-op: outbound email is provisioned by default now. Kept for one release
   *  so runbooks and scripts that still pass it do not hard-fail. */
  enableEmail = Option.Boolean("--enable-email", false, {
    description: "Deprecated no-op — outbound email is provisioned by default. Use --no-email to opt out.",
  });
  noEmail = Option.Boolean("--no-email", false, {
    description: "Deploy without outbound email. The executor renders advisor handoffs but cannot send them.",
  });

  async executeCommand(): Promise<number> {
    if (!NAME_RE.test(this.name)) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Invalid worker name '${this.name}'. Use lowercase letters, digits, and hyphens only.`,
      });
    }
    if (typeof this.brain !== "string" || !this.brain.includes("/")) {
      throw new LocalCliError({
        code: "USAGE",
        message: "--brain owner/repo is required (the executor delivers proof to a brain).",
        hint: "Example: --brain m8t-labs/azure-exec-smoke-brain",
      });
    }

    const size = (this.size ?? "large").toLowerCase();
    const preset = SIZE_PRESETS[size];
    if (!preset) {
      throw new LocalCliError({
        code: "USAGE",
        message: `Unknown --size '${this.size ?? ""}'. Use small, medium, or large.`,
      });
    }

    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const onProgress =
      mode === "pretty" ? (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`) : undefined;

    // Advisories NEVER go to stderr: the installer's retry classifier parses this
    // command's stderr as JSON, and one non-JSON line there makes it read a
    // transient failure as permanent. Pretty mode still shows them - on stdout.
    const warnings = makeWarningSink((m) => {
      if (mode !== "json") this.context.stdout.write(`${colors.hint("note:")} ${m}\n`);
    });

    const credential = new DefaultAzureCredential();
    const account = await getAzAccount();
    const subscriptionId = this.subscription ?? account.subscriptionId;
    const callerObjectId = await getCallerObjectId();

    // Footgun guard: refuse if the platform isn't deployed yet (clear message
    // instead of a cryptic unreachable-Foundry failure downstream).
    await assertPlatformLive({ subscriptionId, interactive });

    const grantScope = this.resolveScope(subscriptionId);

    // Fail fast on the high-privilege grant BEFORE any side effects (deploy, Contributor):
    // an executor that never gets UAA is just Contributor-only (safe), but the operator
    // should learn they lack the identity-plane role before we burn a deploy.
    // `=== true`: clipanion holds a truthy Option descriptor until argv parse, so a
    // bare check would enable this high-privilege grant by default under test construction.
    if (this.grantAccessAdmin === true) {
      const canAssign = await callerCanAssignRoles({ credential, scope: grantScope, callerObjectId });
      if (!canAssign) {
        throw new LocalCliError({
          code: "CANNOT_GRANT_UAA",
          message: `You lack Owner / User Access Administrator at ${grantScope}, so you cannot grant User Access Administrator to the executor.`,
          hint: "Re-run from a principal with Owner or UAA at that scope, or drop --grant-access-admin.",
        });
      }
    }

    if (this.enableEmail === true) {
      warnings.add("--enable-email is a no-op now: outbound email is provisioned by default. Pass --no-email to opt out.");
    }

    const imageInput = this.image ?? DEFAULT_IMAGE;
    const repoRef = imageInput.includes("/") ? imageInput : `${DEFAULT_REGISTRY}/${imageInput}`;
    const requestedImage = `${repoRef}:${this.imageTag ?? DEFAULT_TAG}`;

    const project = await resolveFoundryProject({
      credential,
      subscriptionId,
      interactive,
      endpoint: this.endpoint,
    });

    let currentMetadata: Record<string, string> = {};
    try {
      const current = await getAgentVersion({ credential, projectEndpoint: project.endpoint, agentName: this.name });
      currentMetadata = { ...(current.metadata ?? {}) };
    } catch {
      /* first deploy */
    }

    const staged = await stagePublicImageIfNeeded({ image: requestedImage, project });
    for (const n of staged.notes) {
      warnings.add(n);
    }
    const image = staged.image;

    const processEnv = this.context.env;
    const kvUri = discoverKvUri(processEnv, typeof this.kvUri === "string" ? normalizeKvUri(this.kvUri) : undefined);

    onProgress?.("checking GitHub App health…");
    const health = await checkAppHealth({ credential, kvUri });
    if (!health.ok) {
      throw new LocalCliError({
        code: "APP_HEALTH_FAILED",
        ...appHealthFailureCopy(health),
      });
    }
    const { slug, appId, privateKeyPem } = await readAppSecrets({ credential, kvUri });
    const [owner, repoName] = this.brain.split("/");
    const installationId = await resolveAppInstallation({
      owner,
      repo: repoName,
      slug,
      appId,
      privateKeyPem,
      write: (s) => this.context.stdout.write(s),
    });

    // Outbound email is provisioned by DEFAULT. It used to hinge on `--enable-email`, and
    // because that choice was recorded nowhere, the ezra rename redeployed the executor
    // without it and the capability vanished silently for six weeks. `--no-email` is now
    // the only way to end up without it, and the decision is written to the stamp either
    // way so a later redeploy or converge can honour it.
    const wantEmail = this.noEmail !== true;
    let acsEnv: Record<string, string> = {};

    // 🔴 TWO DIFFERENT resource groups, and conflating them re-creates the original outage.
    //
    //  - The ACS resource must land where the executor's Contributor grant reaches, which is
    //    derived from the GRANT SCOPE alone (see classifyGrantScope for the three ways any
    //    other derivation breaks the invariant).
    //  - The platform stamp lives in the PLATFORM resource group's storage account. Reading
    //    or writing it anywhere else throws PLATFORM_STAMP_STORAGE_NOT_FOUND, which would
    //    read as "no stamp" — and a deploy that wires email into the executor but records
    //    nothing is exactly the state the ezra rename left behind.
    const stampRg = await this.resolvePlatformResourceGroup({ subscriptionId, interactive, warnings });
    const scopeClass = classifyGrantScope(grantScope, subscriptionId);

    if (wantEmail) {
      const suffix = platformSuffixFromKvUri(kvUri);
      const acsRg =
        scopeClass.kind === "resource-group" ? scopeClass.resourceGroup : scopeClass.kind === "subscription" ? stampRg : null;

      if (scopeClass.kind === "unusable") {
        // Never fail the deploy over email — an executor without it is still useful. But say
        // so out loud and say WHY: a silent skip is precisely how this broke before.
        warnings.add(`Outbound email was NOT provisioned: ${scopeClass.reason}. Re-run with --resource-group <rg> to place it explicitly.`);
      } else if (acsRg === null || suffix === null) {
        warnings.add(
          "Outbound email was NOT provisioned: could not work out which resource group it belongs in. " +
            "Re-run with --resource-group <rg>, or turn it on later with `m8t platform email on`.",
        );
      } else {
        // Isolated: a subscription missing the Microsoft.Communication provider registration
        // (the installer registers CognitiveServices/App/Storage/OperationalInsights, not
        // Communication) must not take the whole executor deploy down with it.
        try {
          onProgress?.("resolving the ACS email sender…");
          const acsClient = new CommunicationServiceManagementClient(credential, subscriptionId);
          const resolution = await resolveAcs({
            client: acsClient,
            stamp: stampRg === null ? null : await this.readStampQuietly({ credential, subscriptionId, resourceGroup: stampRg }),
            subscriptionId,
            resourceGroup: acsRg,
            suffix,
            dataLocation: "United States",
            onProgress,
          });
          acsEnv = {
            M8T_ACS_ENDPOINT: resolution.acs.endpoint,
            M8T_ACS_SENDER: resolution.acs.sender,
            M8T_EMAIL_ENABLED: "true",
          };
          await this.recordEmailIntent({
            credential,
            subscriptionId,
            resourceGroup: stampRg,
            feature: emailFeatureFrom(resolution.acs, true),
            warnings,
            onProgress,
          });
        } catch (e) {
          acsEnv = {};
          warnings.add(
            `Outbound email was NOT provisioned (${e instanceof Error ? e.message : String(e)}). ` +
              "The executor is deployed and usable; turn email on later with `m8t platform email on`.",
          );
        }
      }
    } else {
      onProgress?.("--no-email: skipping outbound email.");
      // Record the OFF decision too. An absent flag already reads as off, so this is not
      // what keeps email disabled — it states the intent so a later reader sees a
      // decision rather than a hole.
      await this.recordEmailIntent({
        credential,
        subscriptionId,
        resourceGroup: stampRg,
        feature: { enabled: false, acsEndpoint: "", acsSender: "" },
        warnings,
        onProgress,
      });
    }

    const env: Record<string, string> = {
      MODEL_DEPLOYMENT_NAME: this.modelDeployment ?? DEFAULT_MODEL,
      REASONING_EFFORT: "low",
      GITHUB_APP_INSTALLATION_ID: installationId,
      AZURE_KEYVAULT_URI: kvUri,
      ...acsEnv,
    };
    for (const pair of this.env ?? []) {
      const eq = pair.indexOf("=");
      if (eq <= 0) {
        throw new LocalCliError({
          code: "USAGE",
          message: `Invalid --env '${pair}'. Expected KEY=VALUE.`,
        });
      }
      env[pair.slice(0, eq)] = pair.slice(eq + 1);
    }

    const { warnings: preconditionWarnings } = await checkPreconditions({
      credential,
      subscriptionId,
      project,
      image,
      callerObjectId,
    });
    for (const w of preconditionWarnings) {
      warnings.add(w);
    }

    // `!== true`: run the quota check unless --skip-quota-check was explicitly passed
    // (clipanion's Option descriptor is truthy until argv parse, so `!this.skipQuotaCheck`
    // would skip the check by default under test construction).
    if (this.skipQuotaCheck !== true) {
      const usages = await listModelQuota(project.region);
      const { verdict, quotad } = modelQuotaVerdict(usages, env.MODEL_DEPLOYMENT_NAME);
      if (verdict === "no_quota") {
        throw new LocalCliError({
          code: "MODEL_NO_QUOTA",
          message: `Model '${env.MODEL_DEPLOYMENT_NAME}' has 0 quota in ${project.region}.`,
          hint: `Pick a quota'd model (e.g. ${quotad[0] ?? "gpt-5-mini"}) or --skip-quota-check.`,
        });
      }
    }

    const { persona: personaName, personaVersion } = resolvePersona("azure-executor");

    const result = await deployHostedWorker({
      credential,
      subscriptionId,
      project,
      name: this.name,
      image,
      cpu: preset.cpu,
      memory: preset.memory,
      env,
      metadata: { ...currentMetadata, source: "m8t", kind: "hosted", persona: personaName, personaVersion, productVisibility: "internal" },
      onProgress,
    });

    onProgress?.(`granting Contributor at ${grantScope}…`);
    await grantContributor({
      credential,
      subscriptionId,
      scope: grantScope,
      principalId: result.principalId,
    });

    // `=== true`: only grant UAA when the flag was explicitly passed (the clipanion
    // Option descriptor is truthy until argv parse).
    if (this.grantAccessAdmin === true) {
      // Caller's assign-roles right was verified up front (before the deploy); grant UAA now
      // that the agent identity exists.
      onProgress?.(`granting User Access Administrator at ${grantScope}…`);
      await grantUserAccessAdmin({
        credential,
        subscriptionId,
        scope: grantScope,
        principalId: result.principalId,
      });
    }

    onProgress?.("granting Key Vault Secrets User for delivery…");
    await ensureDeliveryGrant({
      credential,
      subscriptionId,
      principalId: result.principalId,
      kvUri,
    });

    onProgress?.("a2a-enabling as a target…");
    const personaPath = path.join(resolvePlatformRepoRoot("'m8t azure-exec deploy'"), "personas", "azure-executor", "persona.md");
    const bridgeUrl = resolveBridgeUrl({
      flag: typeof this.gatewayUrl === "string" ? this.gatewayUrl : undefined,
      env: processEnv,
    });
    await enableA2a({
      credential,
      projectEndpoint: project.endpoint,
      projectArmId: `${project.accountScope}/projects/${project.projectName}`,
      agentName: this.name,
      personaPath,
      bridgeUrl,
      onProgress,
    });

    if (mode === "json") {
      this.context.stdout.write(
        renderJson({
          name: this.name,
          version: result.version,
          status: result.status,
          persona: personaName,
          image,
          scope: grantScope,
          endpoint: project.endpoint,
          agentPrincipalId: result.principalId,
          warnings: warnings.list(),
        }) + "\n",
      );
      return 0;
    }

    this.context.stdout.write(
      `${colors.success("✓")} deployed Azure executor ${colors.field(this.name)} (version ${result.version}, ${result.status}, ${size}).\n`,
    );
    this.context.stdout.write(`  scope: ${grantScope}\n  image: ${image}\n`);
    this.context.stdout.write(
      this.grantAccessAdmin === true
        ? `  ${colors.hint("next:")} invoke it directly or via a2a; it provisions (Tier 0/1) and executes human-approved Tier-2 (role/delete).\n`
        : `  ${colors.hint("next:")} invoke it directly or via a2a; it provisions (Tier 0/1) and refuses Tier-2.\n`,
    );
    return 0;
  }

  /** Resolve the PLATFORM resource group — the one holding the stamp's storage account.
   *
   *  Deliberately independent of the ACS resource group: they are often the same, but when
   *  they are not, reading the stamp from the wrong one throws and would read as "no stamp".
   *  Returns null (with a warning) rather than throwing, because a worker deploy must not
   *  fail over the stamp. */
  private async resolvePlatformResourceGroup(opts: {
    subscriptionId: string;
    interactive: boolean;
    warnings: { add: (m: string) => void };
  }): Promise<string | null> {
    const disambiguator =
      typeof this.resourceGroup === "string" && this.resourceGroup.length > 0 ? this.resourceGroup : undefined;
    try {
      // `--resource-group` is forwarded as a HINT, because on a subscription holding more
      // than one m8t deployment an unfiltered lookup either prompts the operator mid-deploy —
      // and whichever they pick becomes the resource group whose stamp we WRITE, so a wrong
      // pick is a cross-install write — or throws GATEWAY_DISCOVERY_MULTIPLE non-interactively.
      //
      // 🔴 But it is only a hint. On this command `--resource-group` is the CONTRIBUTOR GRANT
      // SCOPE, not the platform resource group, and a least-privilege deploy legitimately
      // scopes the executor to a workload RG while the platform lives elsewhere. Treating the
      // grant scope as authoritative here would lose the stamp write entirely and send
      // resolveAcs off to provision a duplicate ACS — straight back into the original outage.
      return resourceGroupFromArmId(
        (
          await discoverGateway({
            subscriptionId: opts.subscriptionId,
            interactive: opts.interactive,
            ...(disambiguator !== undefined ? { resourceGroup: disambiguator } : {}),
          })
        ).containerAppResourceId,
      );
    } catch (e) {
      // RG_NOT_FOUND means precisely "that resource group is not a platform RG" — i.e. the
      // legitimate divergent case above. Retry unfiltered before giving up.
      if ((e as { code?: unknown }).code === "GATEWAY_DISCOVERY_RG_NOT_FOUND" && disambiguator !== undefined) {
        try {
          return resourceGroupFromArmId(
            (await discoverGateway({ subscriptionId: opts.subscriptionId, interactive: opts.interactive }))
              .containerAppResourceId,
          );
        } catch (retryErr) {
          opts.warnings.add(
            `Could not locate the platform resource group (${retryErr instanceof Error ? retryErr.message : String(retryErr)}), so the ` +
              "outbound-email decision could not be recorded. Run `m8t platform email on|off` once the platform resolves.",
          );
          return null;
        }
      }
      opts.warnings.add(
        `Could not locate the platform resource group (${e instanceof Error ? e.message : String(e)}), so the ` +
          "outbound-email decision could not be recorded. Run `m8t platform email on|off` once the platform resolves.",
      );
      return null;
    }
  }

  /** Read the platform stamp, distinguishing "genuinely absent" from "unreadable".
   *
   *  🔴 The distinction matters: on `absent` (a fresh install) falling through to ACS
   *  discovery is correct, but on `unreadable` we cannot know whether an ACS is already on
   *  record, and provisioning a duplicate billable resource on a failed read would be worse
   *  than doing nothing. `unreadable` therefore throws, and the caller's catch turns it into
   *  a loud skip. */
  private async readStampQuietly(opts: {
    credential: TokenCredential;
    subscriptionId: string;
    resourceGroup: string;
  }): Promise<InstalledStamp | null> {
    const outcome = await readStampOutcome(opts);
    if (outcome.source === "explicit") return outcome.stamp;
    if (outcome.source === "absent") return null;
    throw new Error(
      "the platform stamp exists but could not be read, so it is not safe to decide whether an ACS is already on record",
    );
  }

  /** Persist the outbound-email decision to the platform stamp.
   *
   *  Recording intent is the durability fix: before this, email lived only in the argv of
   *  one command. If the stamp cannot be read or written, the deploy still succeeds — but
   *  it WARNS, because an intent that silently failed to persist is exactly the state that
   *  hid this outage. */
  private async recordEmailIntent(opts: {
    credential: TokenCredential;
    subscriptionId: string;
    /** The PLATFORM resource group, or null when it could not be resolved. */
    resourceGroup: string | null;
    feature: EmailFeatureStamp;
    warnings: { add: (m: string) => void };
    onProgress?: (m: string) => void;
  }): Promise<void> {
    const { credential, subscriptionId, resourceGroup, feature, warnings, onProgress } = opts;
    if (resourceGroup === null) return; // already warned by resolvePlatformResourceGroup
    try {
      const current = await readStamp({ credential, subscriptionId, resourceGroup });
      if (current === null) {
        warnings.add(
          `Outbound email is ${feature.enabled ? "on" : "off"} for this executor, but there is no platform stamp to record it in — ` +
            "a later converge will not know about it. Run `m8t platform email on` once the stamp exists.",
        );
        return;
      }
      // Keep any ACS identity already on record when the incoming one is blank (the
      // --no-email path), so re-enabling later is a flag flip rather than a rediscovery.
      const prior = current.features?.email;
      const merged: EmailFeatureStamp = {
        enabled: feature.enabled,
        acsEndpoint: feature.acsEndpoint || (prior?.acsEndpoint ?? ""),
        acsSender: feature.acsSender || (prior?.acsSender ?? ""),
      };
      await writeStamp({
        credential,
        subscriptionId,
        resourceGroup,
        stamp: { ...current, features: { ...current.features, email: merged } },
        onProgress,
      });
      onProgress?.(`recorded outbound email = ${merged.enabled ? "on" : "off"} in the platform stamp.`);
    } catch (e) {
      warnings.add(
        `Could not record the outbound-email decision in the platform stamp (${e instanceof Error ? e.message : String(e)}). ` +
          "The executor is wired correctly, but a later converge will not know the intent.",
      );
    }
  }

  private resolveScope(subscriptionId: string): string {
    if (typeof this.scope === "string" && this.scope.startsWith("/subscriptions/")) return this.scope;
    if (typeof this.resourceGroup === "string" && this.resourceGroup.length > 0) {
      return `/subscriptions/${subscriptionId}/resourceGroups/${this.resourceGroup}`;
    }
    throw new LocalCliError({
      code: "USAGE",
      message:
        "A Contributor scope is required. Pass --resource-group <rg> or --scope <full-arm-id>.",
      hint: "This worker is least-privilege: there is no default scope. Scope it to a resource group (or pass a full --scope id).",
    });
  }
}
